// Modal dodawania pojedynczej pozycji do oferty handlowca.
// Wyszukiwarka po `useGetClientProducts` + POST do `usePostSalesCrmOfferPosition`.

import React, { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  MenuItem,
  TextField
} from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

import { WiseModal } from 'components/wise';
import { useGetClientProducts, usePostSalesCrmOfferPosition } from 'api';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/AddOfferPositionModal.module.scss';

interface IAddOfferPositionModalProps {
  open: boolean;
  clientId: number;
  offerId: string;
  onClose: () => void;
  onAdded?: () => void;
  /**
   * Wstępna fraza w wyszukiwarce (np. symbol/nazwa produktu).
   * Używane przy otwarciu modalu z kafelka "Produkty proponowane" - modal sam wyszuka
   * produkt i przy pierwszym trafieniu o pasującym `initialProductId` automatycznie
   * go zaznaczy (wraz z domyślną jednostką).
   */
  initialSearchKeyword?: string;
  /**
   * ID produktu do auto-zaznaczenia po załadowaniu wyników wyszukiwarki.
   */
  initialProductId?: number;
}

/**
 * Miniaturka produktu z fallbackiem na ikone gdy brak URL lub HTTP 404.
 * Wzorzec WiseProductItem (onError + state).
 */
const ResultItemImage: FC<{ src: string | null; imgClassName: string; placeholderClassName: string }> = ({
  src,
  imgClassName,
  placeholderClassName
}) => {
  const [hasError, setHasError] = useState(false);
  if (!src || hasError) {
    return (
      <div className={placeholderClassName}>
        <ImageOutlinedIcon fontSize="inherit" />
      </div>
    );
  }
  return <img src={src} alt="" className={imgClassName} onError={() => setHasError(true)} />;
};

const AddOfferPositionModal: FC<IAddOfferPositionModalProps> = ({
  open,
  clientId,
  offerId,
  onClose,
  onAdded,
  initialSearchKeyword,
  initialProductId
}) => {
  const { t } = useTranslation();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  // Czy oczekujemy na auto-zaznaczenie produktu z initialProductId po załadowaniu wyników.
  // Resetowany po pierwszej zmianie wyszukiwarki przez użytkownika.
  const [pendingInitialSelection, setPendingInitialSelection] = useState(false);

  const { data: searchResult, isFetching } = useGetClientProducts(
    clientId,
    searchKeyword
  );

  const { mutate: addPosition, isLoading: isAdding } = usePostSalesCrmOfferPosition(
    clientId,
    offerId,
    {
      onSuccess: () => {
        resetState();
        onAdded?.();
        onClose();
      },
      onError: (error) => {
        setErrorMessage(error?.message || t('Nie udało się dodać pozycji.'));
      }
    }
  );

  const resetState = () => {
    setSearchKeyword('');
    setSelectedProductId(null);
    setSelectedUnitId(null);
    setQuantity(1);
    setErrorMessage(null);
    setImageFailed(false);
    setPendingInitialSelection(false);
  };

  useEffect(() => {
    setImageFailed(false);
  }, [selectedProductId]);

  // Pre-fill wyszukiwarki przy otwarciu modalu z konkretnym produktem (np. z kafelka "Produkty proponowane").
  useEffect(() => {
    if (!open) return;
    if (initialSearchKeyword && initialSearchKeyword.trim().length > 0) {
      setSearchKeyword(initialSearchKeyword);
      setPendingInitialSelection(initialProductId !== undefined && initialProductId !== null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSearchKeyword, initialProductId]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSubmit = () => {
    if (selectedProductId === null || quantity <= 0) {
      setErrorMessage(t('Wybierz produkt i podaj ilość większą od zera.'));
      return;
    }
    setErrorMessage(null);
    addPosition({
      productId: selectedProductId,
      quantity,
      productUnitId: selectedUnitId ?? undefined
    });
  };

  const products = searchResult?.items ?? [];

  // Auto-zaznaczenie produktu po załadowaniu wyników wyszukiwarki - gdy modal otwarty
  // z konkretnym `initialProductId` (np. kliknięty kafelek z "Produkty proponowane").
  useEffect(() => {
    if (!pendingInitialSelection || isFetching || initialProductId === undefined) return;
    const match = products.find((p) => p.id === initialProductId);
    if (!match) return;
    setSelectedProductId(match.id);
    const units = match.product_unit_short_info?.units ?? [];
    const defaultUnitDictId = match.product_unit_short_info?.default_unit_id ?? null;
    const mainUnit = defaultUnitDictId !== null
      ? units.find((u) => u.unit_id === defaultUnitDictId)
      : undefined;
    setSelectedUnitId(mainUnit?.id ?? units[0]?.id ?? null);
    setPendingInitialSelection(false);
  }, [pendingInitialSelection, isFetching, products, initialProductId]);

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selectedProductId) ?? null,
    [products, selectedProductId]
  );

  const selectedImage =
    selectedProduct?.product_image_formatted_short_info?.images?.[0]?.thumb ??
    selectedProduct?.product_image_formatted_short_info?.images?.[0]?.min ??
    null;

  const selectedName =
    selectedProduct?.product_basic_short_info?.title ??
    selectedProduct?.product_basic_full_info?.title ??
    (selectedProduct ? `Produkt #${selectedProduct.id}` : '');

  const selectedSymbol =
    selectedProduct?.product_basic_short_info?.symbol ??
    selectedProduct?.product_basic_short_info?.index ??
    '';

  const availableUnits = selectedProduct?.product_unit_short_info?.units ?? [];
  const defaultUnitId = selectedProduct?.product_unit_short_info?.default_unit_id ?? null;

  const handleSelectProduct = (productId: number) => {
    setSelectedProductId(productId);
    const product = products.find((p) => p.id === productId);
    const units = product?.product_unit_short_info?.units ?? [];
    const defaultUnitDictId = product?.product_unit_short_info?.default_unit_id ?? null;
    const mainUnit = defaultUnitDictId !== null
      ? units.find((u) => u.unit_id === defaultUnitDictId)
      : undefined;
    setSelectedUnitId(mainUnit?.id ?? units[0]?.id ?? null);
  };

  return (
    <WiseModal open={open} onClose={handleClose} title={t('Dodaj pozycję do oferty')} maxWidth={680}>
      <div className={styles.body}>
        <TextField
          label={t('Wyszukaj produkt (nazwa, symbol, SKU)')}
          value={searchKeyword}
          onChange={(e) => {
            setSearchKeyword(e.target.value);
            setSelectedProductId(null);
          }}
          fullWidth
          size="small"
          autoFocus
        />

        <div className={styles.results}>
          {isFetching && (
            <div className={styles.loading}>
              <CircularProgress size={20} />
            </div>
          )}
          {!isFetching && searchKeyword.trim().length > 0 && products.length === 0 && (
            <div className={styles.empty}>{t('Brak wyników.')}</div>
          )}
          {!isFetching &&
            products.map((p) => {
              const title = p.product_basic_short_info?.title ?? `Produkt #${p.id}`;
              const symbol = p.product_basic_short_info?.symbol ?? p.product_basic_short_info?.index ?? '';
              const thumb =
                p.product_image_formatted_short_info?.images?.[0]?.thumb ??
                p.product_image_formatted_short_info?.images?.[0]?.min ??
                null;
              const isSelected = selectedProductId === p.id;
              return (
                <button
                  type="button"
                  key={p.id}
                  className={`${styles.resultItem} ${isSelected ? styles.resultItemSelected : ''}`}
                  onClick={() => handleSelectProduct(p.id)}>
                  <div className={styles.resultThumbWrapper}>
                    <ResultItemImage
                      src={thumb}
                      imgClassName={styles.resultThumb}
                      placeholderClassName={styles.resultThumbPlaceholder}
                    />
                  </div>
                  <div className={styles.resultText}>
                    <div className={styles.resultName}>{title}</div>
                    {symbol && <div className={styles.resultSymbol}>{symbol}</div>}
                  </div>
                </button>
              );
            })}
        </div>

        {selectedProduct && (
          <div className={styles.selectedPreview}>
            <div className={styles.selectedHeader}>
              <div className={styles.selectedImageWrapper}>
                {selectedImage && !imageFailed ? (
                  <img
                    src={selectedImage}
                    alt={selectedName}
                    className={styles.selectedImage}
                    onError={() => setImageFailed(true)}
                  />
                ) : (
                  <div className={styles.selectedImagePlaceholder} aria-label={t('Brak grafiki')}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="m21 15-5-5L5 21" />
                    </svg>
                  </div>
                )}
              </div>
              <div className={styles.selectedInfo}>
                <div className={styles.selectedName}>{selectedName}</div>
                {selectedSymbol && (
                  <div className={styles.selectedSymbol}>{selectedSymbol}</div>
                )}
              </div>
            </div>

            <div className={styles.qtyRow}>
              <TextField
                label={t('Jednostka')}
                select
                value={selectedUnitId ?? ''}
                onChange={(e) =>
                  setSelectedUnitId(e.target.value === '' ? null : Number(e.target.value))
                }
                size="small"
                sx={{ minWidth: 160, flex: 1 }}
                disabled={availableUnits.length === 0}>
                {availableUnits.length === 0 && (
                  <MenuItem value="" disabled>
                    {t('Brak jednostek')}
                  </MenuItem>
                )}
                {availableUnits.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                    {u.unit_id === defaultUnitId ? ` (${t('domyślna')})` : ''}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('Ilość')}
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(parseFloat(e.target.value))}
                size="small"
                inputProps={{ min: 1, step: 1 }}
                sx={{ width: 140 }}
              />
            </div>
          </div>
        )}

        {errorMessage && <div className={styles.error}>{errorMessage}</div>}

        <div className={styles.actions}>
          <Button onClick={handleClose} variant="text">
            {t('Anuluj')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={selectedProductId === null || isAdding}
            sx={{ backgroundColor: '#6b54ff', '&:hover': { backgroundColor: '#5a47e0' } }}>
            {isAdding ? <CircularProgress size={18} /> : t('Dodaj')}
          </Button>
        </div>
      </div>
    </WiseModal>
  );
};

export default AddOfferPositionModal;
